import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, User, Package, Palette, Wallet, Gavel } from "lucide-react";
import { api, formatPrice, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArtworkManager } from "../components/ArtworkManager";

export default function Account() {
  const { user, loading, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("profil");
  const [orders, setOrders] = useState([]);
  const [sales, setSales] = useState([]);
  const [wonAuctions, setWonAuctions] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/connexion");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "", phone: user.phone || "",
        address_line: user.address_line || "", city: user.city || "",
        postal_code: user.postal_code || "", country: user.country || "France",
        building: user.building || "", building_code: user.building_code || "",
        bio: user.bio || "", iban: user.iban || "", bic: user.bic || "", bank_holder: user.bank_holder || "",
        style: user.style || "", personality: user.personality || "",
        photo: user.photo || (user.photos && user.photos[0]) || "",
        photos: user.photos || [],
      });
      if (user.role === "customer") {
        api.get("/my/orders").then((r) => setOrders(r.data)).catch(() => {});
        api.get("/my/won-auctions").then((r) => setWonAuctions(r.data)).catch(() => {});
      }
      if (user.role === "artist") api.get("/artist/sales").then((r) => setSales(r.data)).catch(() => {});
    }
  }, [user]);

  if (loading || !user || !form) return <div className="pt-40 text-center text-zinc-500">Chargement…</div>;

  const isArtist = user.role === "artist";

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile(form);
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const up = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const tabs = isArtist
    ? [["profil", "Profil", User], ["oeuvres", "Mes œuvres", Palette], ["ventes", "Mes ventes", Wallet]]
    : [["profil", "Profil", User], ["commandes", "Mes commandes", Package], ["encheres", "Mes enchères gagnées", Gavel]];

  return (
    <div className="min-h-screen pt-28 pb-24 max-w-5xl mx-auto px-6 lg:px-10" data-testid="account-page">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="font-display text-5xl text-white tracking-tight">Bonjour, {user.name}</h1>
          <p className="text-zinc-500 text-sm mt-1">{user.email} · {isArtist ? "Compte artiste" : "Compte client"}</p>
        </div>
        <button data-testid="logout-button" onClick={() => { logout(); navigate("/"); }}
          className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white px-5 py-2.5 text-sm transition-colors">
          <LogOut className="h-4 w-4" /> Déconnexion
        </button>
      </div>

      <div className="flex gap-2 border-b border-white/10 mb-8 flex-wrap">
        {tabs.map(([key, label, Icon]) => (
          <button key={key} data-testid={`tab-${key}`} onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 px-5 py-3 text-sm transition-colors border-b-2 -mb-px ${tab === key ? "border-[#2563EB] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "profil" && (
        <form onSubmit={saveProfile} className="max-w-xl space-y-4" data-testid="profile-form">
          <Field testid="profile-name" label="Nom" value={form.name} onChange={up("name")} />
          <Field testid="profile-phone" label="Téléphone" value={form.phone} onChange={up("phone")} />
          {!isArtist && (
            <>
              <Field testid="profile-photo-customer" label="Photo de profil (URL)" value={form.photo} onChange={up("photo")} />
              <Field testid="profile-address" label="Adresse" value={form.address_line} onChange={up("address_line")} />
              <div className="grid grid-cols-2 gap-4">
                <Field testid="profile-postal" label="Code postal" value={form.postal_code} onChange={up("postal_code")} />
                <Field testid="profile-city" label="Ville" value={form.city} onChange={up("city")} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field testid="profile-building" label="Bâtiment / Étage" value={form.building} onChange={up("building")} />
                <Field testid="profile-building-code" label="Code d'entrée / interphone" value={form.building_code} onChange={up("building_code")} />
              </div>
              <Field testid="profile-country" label="Pays" value={form.country} onChange={up("country")} />
            </>
          )}
          {isArtist && (
            <>
              <Field testid="profile-style" label="Style artistique" value={form.style} onChange={up("style")} />
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500">Présentation</label>
                <textarea data-testid="profile-bio" value={form.bio} onChange={up("bio")} rows={4}
                  className="w-full bg-[#0d0d0d] border border-white/15 px-4 py-3 text-white mt-1 focus:border-[#2563EB] outline-none resize-none" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500">Votre personnalité</label>
                <textarea data-testid="profile-personality" value={form.personality} onChange={up("personality")} rows={2}
                  className="w-full bg-[#0d0d0d] border border-white/15 px-4 py-3 text-white mt-1 focus:border-[#2563EB] outline-none resize-none" />
              </div>
              <Field testid="profile-photo" label="Photo (URL)" value={(form.photos && form.photos[0]) || ""} onChange={(e) => setForm({ ...form, photos: [e.target.value] })} />
              <div className="border border-white/10 p-4 space-y-4">
                <p className="text-xs uppercase tracking-widest text-[#60a5fa]">Coordonnées bancaires</p>
                <Field testid="profile-holder" label="Titulaire" value={form.bank_holder} onChange={up("bank_holder")} />
                <Field testid="profile-iban" label="IBAN" value={form.iban} onChange={up("iban")} />
                <Field testid="profile-bic" label="BIC" value={form.bic} onChange={up("bic")} />
              </div>
            </>
          )}
          <button data-testid="save-profile" type="submit" disabled={saving}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white px-8 py-3 text-sm transition-colors">
            Enregistrer
          </button>
        </form>
      )}

      {tab === "oeuvres" && isArtist && <ArtworkManager defaultArtistName={user.name} />}

      {tab === "ventes" && isArtist && (
        <div data-testid="sales-tab">
          {sales.length === 0 ? (
            <p className="text-zinc-500">Aucune vente pour le moment.</p>
          ) : (
            <div className="space-y-4">
              {sales.map((s, i) => (
                <div key={`${s.date}-${s.title}-${i}`} className="border border-white/10 bg-[#0d0d0d] p-5" data-testid={`sale-${i}`}>
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <p className="text-white text-lg font-display">{s.title}</p>
                      <p className="text-zinc-500 text-sm mt-0.5">
                        {s.date ? new Date(s.date).toLocaleDateString("fr-FR") : ""}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-emerald-400 text-lg">{formatPrice(s.price)}</p>
                      <span className={`text-xs uppercase tracking-wide ${s.payment_status === "paid" ? "text-emerald-400" : "text-zinc-500"}`}>
                        {s.payment_status === "paid" ? "Paiement effectué" : "En attente"}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/10 grid sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Acheteur</p>
                      <p className="text-zinc-200">{s.customer_name || "—"}</p>
                      <p className="text-zinc-400">{s.customer_email}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Adresse de livraison</p>
                      {s.shipping_address?.line || s.shipping_address?.city ? (
                        <p className="text-zinc-200 leading-relaxed">
                          {s.shipping_address.line}
                          {s.shipping_address.line && <br />}
                          {s.shipping_address.postal_code} {s.shipping_address.city}
                          {(s.shipping_address.postal_code || s.shipping_address.city) && <br />}
                          {s.shipping_address.country}
                          {(s.shipping_address.building || s.shipping_address.building_code) && (
                            <><br />Bât. {s.shipping_address.building || "—"} · Code {s.shipping_address.building_code || "—"}</>
                          )}
                          {s.shipping_address.phone && <><br />Tél. {s.shipping_address.phone}</>}
                        </p>
                      ) : (
                        <p className="text-zinc-500">Non renseignée par l'acheteur</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "commandes" && !isArtist && (
        <div data-testid="orders-tab">
          {orders.length === 0 ? (
            <p className="text-zinc-500">Vous n'avez pas encore de commande.</p>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.session_id} className="border border-white/10 bg-[#0d0d0d] p-5 flex flex-wrap justify-between items-center gap-4">
                  <div>
                    <p className="text-white">{o.titles?.join(", ")}</p>
                    <p className="text-zinc-500 text-sm">{new Date(o.created_at).toLocaleDateString("fr-FR")}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white">{formatPrice(o.amount)}</p>
                    <span className={`text-xs uppercase tracking-wide ${o.payment_status === "paid" ? "text-emerald-400" : "text-zinc-500"}`}>
                      {o.payment_status === "paid" ? "Payée" : "En attente"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {tab === "encheres" && !isArtist && (
        <div data-testid="won-auctions-tab">
          {wonAuctions.length === 0 ? (
            <p className="text-zinc-500">Vous n'avez remporté aucune enchère pour l'instant.</p>
          ) : (
            <div className="space-y-3">
              {wonAuctions.map((w) => (
                <div key={w.id} className="border border-white/10 bg-[#0d0d0d] p-4 flex flex-wrap items-center gap-4" data-testid={`won-auction-${w.id}`}>
                  <img src={w.image_url} alt={w.title} loading="lazy" className="h-16 w-16 object-cover shrink-0" />
                  <div className="flex-1 min-w-[140px]">
                    <p className="text-white">{w.title}</p>
                    <p className="text-zinc-500 text-sm">{w.total.toFixed(2)} {w.currency} (œuvre + livraison)</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs uppercase tracking-wide ${w.paid ? "text-emerald-400" : w.order_confirmed ? "text-amber-400" : "text-zinc-400"}`}>
                      {w.paid ? "Réglée" : w.order_confirmed ? "Commande passée" : "À régler"}
                    </span>
                    {!w.paid && (
                      <button
                        data-testid={`pay-won-${w.id}`}
                        onClick={() => navigate("/encheres")}
                        className="block mt-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 text-xs transition-colors"
                      >
                        {w.order_confirmed ? "Voir / Payer" : "Régler maintenant"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-zinc-500">{label}</label>
    <input data-testid={testid} value={value} onChange={onChange}
      className="w-full bg-[#0d0d0d] border border-white/15 px-4 py-3 text-white mt-1 focus:border-[#2563EB] outline-none" />
  </div>
);
